<!-- Backdrop (mobile only) - z-[100] to cover all main content (header z-50, prompt z-50) -->
<div
  *ngIf="isOpen"
  class="fixed inset-0 bg-black/50 z-[100] md:hidden transition-opacity duration-200"
  [class.opacity-100]="isOpen"
  [class.opacity-0]="!isOpen"
  (click)="onBackdropClick()"
  aria-hidden="true">
</div>

<!-- FIN-0106: Sidebar with Circle-First Design
     - When open: z-[101] to be above header (z-50) and backdrop (z-[100])
     - When closed: invisible to prevent interference with header
     FIN-0108: Dark mode uses base background, elevated surface for buttons
-->
<aside
  class="fixed top-0 left-0 h-full bg-white dark:bg-dark-base shadow-xl dark:shadow-black/30 transition-all duration-smooth ease-smooth-out flex flex-col"
  [class.translate-x-0]="isOpen"
  [class.-translate-x-full]="!isOpen"
  [class.z-[101]]="isOpen"
  [class.z-0]="!isOpen"
  [class.invisible]="!isOpen"
  [class.visible]="isOpen"
  [class.w-[80vw]]="true"
  [class.md:w-72]="true"
  role="navigation"
  aria-label="Chat history">

  <!-- Header -->
  <div class="flex-none border-b border-gray-100 dark:border-dark-border p-3">
    <div class="flex items-center justify-between">
      <!-- New Chat Button - pill shape -->
      <button
        type="button"
        class="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100 dark:bg-dark-elevated hover:bg-gray-200 dark:hover:bg-dark-surface active:scale-95 transition-all duration-fast ease-snap will-change-transform"
        (click)="createNewConversation()">
        <svg class="w-5 h-5 text-gray-600 dark:text-dark-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        <span class="text-sm font-medium text-gray-700 dark:text-dark-text-primary">New Chat</span>
      </button>

      <!-- Close Button - circle -->
      <button
        type="button"
        class="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-dark-surface active:scale-95 transition-all duration-fast ease-snap will-change-transform"
        (click)="close()"
        aria-label="Close sidebar">
        <svg class="w-5 h-5 text-gray-500 dark:text-dark-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>
  </div>

  <!-- Conversation List Container -->
  <div class="flex-1 flex flex-col min-h-0">

    <!-- Loading state (first load) -->
    <div *ngIf="loading && conversations.length === 0" class="p-4 space-y-3">
      <div *ngFor="let i of [1,2,3,4,5]" class="animate-pulse">
        <div class="h-4 bg-gray-200 dark:bg-dark-surface rounded w-3/4 mb-2"></div>
        <div class="h-3 bg-gray-100 dark:bg-dark-elevated rounded w-1/2"></div>
      </div>
    </div>

    <!-- Empty state -->
    <div *ngIf="!loading && conversations.length === 0 && !error" class="p-6 text-center">
      <svg class="w-12 h-12 mx-auto text-gray-300 dark:text-dark-text-muted mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
      </svg>
      <p class="text-gray-500 dark:text-dark-text-secondary text-sm">No conversations yet</p>
      <p class="text-gray-400 dark:text-dark-text-tertiary text-xs mt-1">Start a new chat to begin</p>
    </div>

    <!-- Error state -->
    <div *ngIf="error" class="p-4 bg-red-50 dark:bg-red-900/30 m-3 rounded-card">
      <p class="text-red-600 dark:text-red-400 text-sm">{{ error }}</p>
      <button
        type="button"
        class="mt-2 px-3 py-1 text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-800/50 rounded-full hover:bg-red-200 dark:hover:bg-red-800 transition-all duration-fast"
        (click)="retry()">
        Try again
      </button>
    </div>

    <!-- Virtual Scroll Conversation List -->
    <cdk-virtual-scroll-viewport
      *ngIf="conversations.length > 0"
      itemSize="56"
      class="flex-1"
      (scrolledIndexChange)="onScrollIndexChange($event)">
      <div
        *cdkVirtualFor="let conversation of conversations; trackBy: trackByConversationId"
        class="relative h-14">

        <!-- Delete confirmation overlay (desktop only - mobile uses bottom sheet) -->
        <div *ngIf="deleteConfirmId === conversation.id"
             class="absolute inset-0 bg-white dark:bg-dark-base z-10 hidden md:flex items-center justify-center p-2 border-b border-gray-100 dark:border-dark-border"
             (click)="$event.stopPropagation()">
          <div class="text-center">
            <p class="text-sm mb-2"
               [ngClass]="{
                 'text-gray-700 dark:text-dark-text-primary': !deleteError,
                 'text-red-600 dark:text-red-400': deleteError
               }">
              {{ deleteError || 'Delete this conversation?' }}
            </p>
            <div class="flex gap-2 justify-center">
              <button
                type="button"
                class="px-4 py-1.5 text-sm bg-red-500 text-white rounded-full hover:bg-red-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-fast"
                [disabled]="deletingId === conversation.id"
                (click)="confirmDelete(conversation.id); $event.stopPropagation()">
                {{ deletingId === conversation.id ? 'Deleting...' : 'Delete' }}
              </button>
              <button
                type="button"
                class="px-4 py-1.5 text-sm bg-gray-100 dark:bg-dark-elevated text-gray-700 dark:text-dark-text-primary rounded-full hover:bg-gray-200 dark:hover:bg-dark-surface active:scale-95 disabled:opacity-50 transition-all duration-fast"
                [disabled]="deletingId === conversation.id"
                (click)="cancelDelete(); $event.stopPropagation()">
                Cancel
              </button>
            </div>
          </div>
        </div>

        <!-- Conversation item - pill shape for selection -->
        <!-- FIN-0108: Dark mode uses muted brand for active state -->
        <div
          class="px-3 py-2 mx-2 rounded-full cursor-pointer flex items-center justify-between group transition-all duration-fast"
          [ngClass]="{
            'bg-brand-50 dark:bg-dark-brand-muted/30': isActive(conversation.id),
            'hover:bg-gray-50 dark:hover:bg-dark-surface': !isActive(conversation.id)
          }"
          (click)="selectConversation(conversation)">

          <!-- Title section -->
          <div class="flex-1 min-w-0 pr-2">
            <!-- Edit mode -->
            <div *ngIf="editingId === conversation.id" class="flex items-center gap-1">
              <input
                type="text"
                [(ngModel)]="editTitle"
                class="flex-1 px-3 py-1 text-sm bg-white dark:bg-dark-elevated dark:text-dark-text-primary border border-brand-200 dark:border-dark-brand-muted rounded-full focus:outline-hidden focus:ring-2 focus:ring-brand/30 dark:focus:ring-dark-brand/30"
                (keydown)="onEditKeydown($event, conversation.id)"
                (click)="$event.stopPropagation()"
                autofocus>
              <button
                type="button"
                class="w-7 h-7 flex items-center justify-center text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-full transition-all duration-fast"
                (click)="saveTitle(conversation.id); $event.stopPropagation()">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </button>
              <button
                type="button"
                class="w-7 h-7 flex items-center justify-center text-gray-400 dark:text-dark-text-tertiary hover:bg-gray-100 dark:hover:bg-dark-surface rounded-full transition-all duration-fast"
                (click)="cancelEditing(); $event.stopPropagation()">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Display mode -->
            <div *ngIf="editingId !== conversation.id">
              <p class="text-sm text-gray-800 dark:text-dark-text-primary truncate"
                 [class.font-medium]="isActive(conversation.id)">
                {{ conversation.title }}
              </p>
              <p class="text-xs text-gray-400 dark:text-dark-text-tertiary mt-0.5">
                {{ formatRelativeTime(conversation.updatedAt) }}
              </p>
            </div>
          </div>

          <!-- Menu button - circle -->
          <div *ngIf="editingId !== conversation.id"
               class="flex-none opacity-0 group-hover:opacity-100 transition-opacity duration-fast"
               [class.opacity-100]="menuOpenId === conversation.id">
            <button
              type="button"
              class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-dark-surface transition-all duration-fast"
              (click)="toggleMenu(conversation.id, $event)"
              aria-label="Conversation options">
              <svg class="w-4 h-4 text-gray-500 dark:text-dark-text-secondary" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading more indicator (inside viewport for proper scrolling) -->
      <div *ngIf="loading" class="p-4 text-center h-14 flex items-center justify-center">
        <div class="inline-flex items-center gap-2 text-gray-500 dark:text-dark-text-secondary text-sm">
          <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading more...
        </div>
      </div>

      <!-- End of list -->
      <div *ngIf="!hasMore && !loading" class="p-4 text-center text-gray-400 dark:text-dark-text-tertiary text-xs h-10">
        End of conversations
      </div>
    </cdk-virtual-scroll-viewport>
  </div>
</aside>

<!-- FIN-0106: Floating dropdown menu with card styling - z-[102] above sidebar -->
<!-- FIN-0108: Dark mode uses elevated surface -->
<div *ngIf="menuOpenId && getMenuConversation() as conversation"
     class="fixed w-40 bg-white dark:bg-dark-elevated rounded-card shadow-elevated dark:shadow-black/30 border border-gray-100 dark:border-dark-border py-1.5 z-[102] animate-message-in"
     [style.top.px]="menuPosition.top"
     [style.right.px]="menuPosition.right">
  <button
    type="button"
    class="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-surface flex items-center gap-2 transition-colors duration-fast"
    (click)="startEditing(conversation, $event)">
    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
    </svg>
    Rename
  </button>
  <button
    type="button"
    class="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2 transition-colors duration-fast"
    (click)="showDeleteConfirm(conversation.id, $event)">
    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
    </svg>
    Delete
  </button>
</div>

<!-- FIN-0106: Mobile bottom sheet with pill buttons - z-[103] above floating menu -->
<!-- FIN-0108: Dark mode uses elevated surface -->
<div *ngIf="deleteConfirmId"
     class="fixed inset-0 z-[103] md:hidden"
     (click)="cancelDelete()">
  <!-- Backdrop -->
  <div class="absolute inset-0 bg-black/50"></div>

  <!-- Bottom sheet -->
  <div class="absolute bottom-0 left-0 right-0 bg-white dark:bg-dark-elevated rounded-t-3xl p-4 pb-8 safe-area-bottom animate-message-in"
       (click)="$event.stopPropagation()">
    <div class="w-12 h-1 bg-gray-300 dark:bg-dark-border-light rounded-full mx-auto mb-4"></div>

    <p class="text-center mb-4"
       [ngClass]="{
         'text-gray-700 dark:text-dark-text-primary': !deleteError,
         'text-red-600 dark:text-red-400': deleteError
       }">
      {{ deleteError || 'Delete this conversation?' }}
    </p>

    <div class="flex flex-col gap-3">
      <button
        type="button"
        class="w-full py-3.5 text-base font-medium bg-red-500 text-white rounded-full hover:bg-red-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-fast will-change-transform"
        [disabled]="deletingId === deleteConfirmId"
        (click)="confirmDelete(deleteConfirmId)">
        {{ deletingId === deleteConfirmId ? 'Deleting...' : 'Delete' }}
      </button>
      <button
        type="button"
        class="w-full py-3.5 text-base font-medium bg-gray-100 dark:bg-dark-surface text-gray-700 dark:text-dark-text-primary rounded-full hover:bg-gray-200 dark:hover:bg-dark-border-light active:scale-[0.98] disabled:opacity-50 transition-all duration-fast will-change-transform"
        [disabled]="deletingId === deleteConfirmId"
        (click)="cancelDelete()">
        Cancel
      </button>
    </div>
  </div>
</div>
